/**
 * 设备管理 API 服务
 * v8.0 新增：IoT 设备管理功能
 * 调用 axiom-agent-admin 服务
 */

import { ApiClient, apiClients } from '@/shared/services/api'
import type { SuccessResponse } from '@/shared/types/api'
import type {
  Device,
  DeviceListResponse,
  DeviceListParams,
  DeviceBatchCreateRequest,
  DeviceBatchCreateResponse,
  DeviceUpdateRequest,
  DeviceExtendRequest,
  DeviceBatchOperationRequest,
  DeviceBatchOperationResponse,
  DeviceResetSecretResponse,
  DeviceTestTokenResponse,
} from '@/modules/agent/types/device'

/**
 * 设备管理 API 服务
 */
export class DeviceApiService {
  /**
   * 批量生成设备
   */
  static async batchCreate(
    projectId: string,
    data: DeviceBatchCreateRequest
  ): Promise<SuccessResponse<DeviceBatchCreateResponse>> {
    return ApiClient.postWithClient<DeviceBatchCreateResponse>(
      apiClients.agentAdmin,
      `/projects/${projectId}/devices/batch`,
      data
    )
  }

  /**
   * 导出 Excel
   * 返回 Blob 数据用于下载
   */
  static async exportExcel(batchNo: string): Promise<Blob> {
    const response = await apiClients.agentAdmin.get(
      `/devices/batch/${batchNo}/export`,
      { responseType: 'blob' }
    )
    return response.data
  }

  /**
   * 获取设备列表
   */
  static async list(
    projectId: string,
    params?: DeviceListParams
  ): Promise<SuccessResponse<DeviceListResponse>> {
    return ApiClient.getWithClient<DeviceListResponse>(
      apiClients.agentAdmin,
      `/projects/${projectId}/devices`,
      { params }
    )
  }

  /**
   * 获取设备详情
   */
  static async get(
    projectId: string,
    deviceId: string
  ): Promise<SuccessResponse<Device>> {
    return ApiClient.getWithClient<Device>(
      apiClients.agentAdmin,
      `/projects/${projectId}/devices/${deviceId}`
    )
  }

  /**
   * 更新设备
   */
  static async update(
    projectId: string,
    deviceId: string,
    data: DeviceUpdateRequest
  ): Promise<SuccessResponse<Device>> {
    return ApiClient.putWithClient<Device>(
      apiClients.agentAdmin,
      `/projects/${projectId}/devices/${deviceId}`,
      data
    )
  }

  /**
   * 吊销设备
   */
  static async block(
    projectId: string,
    deviceId: string
  ): Promise<SuccessResponse<Device>> {
    return ApiClient.postWithClient<Device>(
      apiClients.agentAdmin,
      `/projects/${projectId}/devices/${deviceId}/block`
    )
  }

  /**
   * 解除吊销
   */
  static async unblock(
    projectId: string,
    deviceId: string
  ): Promise<SuccessResponse<Device>> {
    return ApiClient.postWithClient<Device>(
      apiClients.agentAdmin,
      `/projects/${projectId}/devices/${deviceId}/unblock`
    )
  }

  /**
   * 重置密钥
   */
  static async resetSecret(
    projectId: string,
    deviceId: string
  ): Promise<SuccessResponse<DeviceResetSecretResponse>> {
    return ApiClient.postWithClient<DeviceResetSecretResponse>(
      apiClients.agentAdmin,
      `/projects/${projectId}/devices/${deviceId}/reset-secret`
    )
  }

  /**
   * 获取设备测试 Token
   * v8.2 新增：设备可用性测试功能
   */
  static async getTestToken(
    projectId: string,
    deviceId: string,
    expiresIn: number = 300
  ): Promise<SuccessResponse<DeviceTestTokenResponse>> {
    return ApiClient.postWithClient<DeviceTestTokenResponse>(
      apiClients.agentAdmin,
      `/projects/${projectId}/devices/${deviceId}/test-token?expires_in=${expiresIn}`
    )
  }

  /**
   * 延长有效期
   */
  static async extend(
    projectId: string,
    deviceId: string,
    data: DeviceExtendRequest
  ): Promise<SuccessResponse<Device>> {
    return ApiClient.postWithClient<Device>(
      apiClients.agentAdmin,
      `/projects/${projectId}/devices/${deviceId}/extend`,
      data
    )
  }

  /**
   * 删除设备
   */
  static async delete(
    projectId: string,
    deviceId: string
  ): Promise<SuccessResponse<{ deleted: boolean; device_id: string }>> {
    return ApiClient.deleteWithClient<{ deleted: boolean; device_id: string }>(
      apiClients.agentAdmin,
      `/projects/${projectId}/devices/${deviceId}`
    )
  }

  /**
   * 按订单批量吊销
   */
  static async batchBlock(
    projectId: string,
    data: DeviceBatchOperationRequest
  ): Promise<SuccessResponse<DeviceBatchOperationResponse>> {
    return ApiClient.postWithClient<DeviceBatchOperationResponse>(
      apiClients.agentAdmin,
      `/projects/${projectId}/devices/batch-block`,
      data
    )
  }

  /**
   * 按订单批量解除吊销
   */
  static async batchUnblock(
    projectId: string,
    data: DeviceBatchOperationRequest
  ): Promise<SuccessResponse<DeviceBatchOperationResponse>> {
    return ApiClient.postWithClient<DeviceBatchOperationResponse>(
      apiClients.agentAdmin,
      `/projects/${projectId}/devices/batch-unblock`,
      data
    )
  }

  /**
   * 按订单批量删除
   */
  static async batchDelete(
    projectId: string,
    data: DeviceBatchOperationRequest
  ): Promise<SuccessResponse<DeviceBatchOperationResponse>> {
    return ApiClient.deleteWithClient<DeviceBatchOperationResponse>(
      apiClients.agentAdmin,
      `/projects/${projectId}/devices/batch`,
      { data }
    )
  }
}

/**
 * 下载 Excel 文件辅助函数
 */
export async function downloadDeviceExcel(batchNo: string, filename?: string): Promise<void> {
  const blob = await DeviceApiService.exportExcel(batchNo)
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename || `devices_${batchNo}.xlsx`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  window.URL.revokeObjectURL(url)
}
